import { GetObjectCommand, PutObjectCommand, DeleteObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { REGION, IMAGE_BUCKET } from '@/aws-exports';
import { getSdkCredentials } from '@/utils/sdk';
import { errorMsg } from '@/utils/message';
import { errorNotify } from '@/utils/errorNotification';

/**
 * @typedef {object} Result
 * @property {number} status - 実行結果のステータス
 * @property {any}data - 実行結果のデータ
 */

const IMAGE_BUCKET_NAME = IMAGE_BUCKET[import.meta.env.MODE];

async function _getS3Client() {
  const sdkCredentials = await getSdkCredentials();
  return new S3Client({
    region: REGION,
    credentials: sdkCredentials,
  });
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.objKey アップロードするオブジェクトのキー
 * @param {File} data.data アップロードするファイルオブジェクト
 * @return {Promise<Result>} 実行結果のオブジェクト
 */
export async function putImageObject(data) {
  let res = { status: 0 };
  try {
    const client = await _getS3Client();
    const command = new PutObjectCommand({
      Bucket: IMAGE_BUCKET_NAME,
      Key: data.objKey,
      Body: data.data,
    });
    await client.send(command);
  } catch {
    res = {
      status: -4,
      data: [{ cause: errorMsg.e_file_upload_failed }],
    };
  }
  return res;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.objKey ダウンロードするオブジェクトのキー
 * @return {Promise<Blob>} 画像ファイルオブジェクト
 */
export async function getImageObject(data) {
  let blob = null;
  try {
    const client = await _getS3Client();
    const command = new GetObjectCommand({ Bucket: IMAGE_BUCKET_NAME, Key: data.objKey });
    const response = await client.send(command);
    if (response.Body) {
      blob = new Blob([await response.Body.transformToByteArray()]);
    }
  } catch {
    //
  }
  return blob;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.objKey 削除するオブジェクトのキー
 * @return {Promise<Result>} 実行結果のオブジェクト
 */
export async function deleteImageObject(data) {
  let res = { status: 0 };
  try {
    const client = await _getS3Client();
    const command = new DeleteObjectCommand({ Bucket: IMAGE_BUCKET_NAME, Key: data.objKey });
    await client.send(command);
  } catch {
    res = { status: -4 };
  }
  return res;
}

/**
 * @param {string} bucketName S3バケット名
 * @param {string} objKey ダウンロードするオブジェクトのキー
 * @return {Promise<String>} 署名付きURL
 */
export async function getDownloadUrl(bucketName, objKey, expiresInSec = 300) {
  let url = '';
  try {
    const client = await _getS3Client();
    const command = new GetObjectCommand({ Bucket: bucketName, Key: objKey });
    url = await getSignedUrl(client, command, { expiresIn: expiresInSec });
  } catch {
    const res = {
      status: -4,
      data: [{ cause: errorMsg.e_signed_url_failed }],
    };
    errorNotify(res);
  }
  return url;
}
